/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useState } from "react";

import classNames from "classnames";
import QRCode from "qrcode";
import toast from "react-hot-toast";

import useClipboard from "hooks/useClipboard";
import useTryCatch from "hooks/useTryCatch";
import { getQrCode } from "lib/v3/loginAPI";

import { QRSkeleton } from "components/common";
import { GoBackIcon } from "components/icons";
import { DuplicateLiteIcon } from "components/icons/common";
import Stack from "components/ui/Stack";
import Text from "components/ui/Text";
import { Button } from "components/ui/Button";
import { Input } from "components/ui/Input";
import ImageViewer from "components/ui/ImageViewer";

const ScanQrCode = ({ setCurrentForm }: any) => {
  const [secretCode, setSecretCode] = useState<string>("");
  const [qrImage, setQrImage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const createQr = useTryCatch(async () => {
    const { data } = await getQrCode();
    setSecretCode(data.secret);
    const QR = await QRCode.toDataURL(data.qr_code);
    setQrImage(QR);
  }, setIsLoading);

  useEffect(() => {
    createQr();
  }, []);

  const copyToClipboard = useClipboard();

  const copySecretCode = () => {
    toast("Copied to clipboard", {
      position: "bottom-center",
    });

    copyToClipboard(secretCode);
  };

  return (
    <form>
      <Stack className="space-y-10">
        <Stack className="space-y-4">
          <Stack className="gap-1">
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentForm("otp_setup")}>
                <GoBackIcon fill="#fff" width="20" height="20" />
              </button>

              <h3 className="text-white text-xl font-bold">
                Scan QR Code or Enter Secret Key
              </h3>
            </div>

            <Text as="p" className="text-base !text-[#989898]">
              To link your account with the 2FA app, you can either scan the QR
              code or manually enter the secret key provided below into the app.
            </Text>
          </Stack>

          <Stack className="flex items-center bg-white py-10">
            {isLoading ? (
              <QRSkeleton />
            ) : (
              <ImageViewer
                src={qrImage}
                alt="qr-image"
                className={classNames(
                  "w-[200px] h-[200px]",
                  isLoading && "animate-pulse",
                )}
                fit="fill"
              />
            )}
          </Stack>

          <Text as="p" className="text-[#BABABA]">
            Alternatively, you can use the secret key.
          </Text>

          <div className="relative flex justify-between">
            <Input isLoading={isLoading} value={secretCode} />

            {secretCode && !isLoading && (
              <Button
                variant="unstyled"
                className="absolute top-[5px] right-1 text-white w-[40px] h-[40px]"
                onClick={() => copySecretCode()}
              >
                <DuplicateLiteIcon width={20} height={20} />
              </Button>
            )}
          </div>
        </Stack>

        <div>
          <Button
            variant="primary"
            type="submit"
            size="xl"
            id="nextConfirmBtn"
            onClick={() => {
              setCurrentForm("confirmation_code");
            }}
          >
            Next
          </Button>
        </div>
      </Stack>
    </form>
  );
};

export default ScanQrCode;
